import { COMMAND_DEFINITIONS } from '../commands/commandModel.js'
import { normalizeCommandBindingPreference } from './commandPreferenceStore.js'
import { validSharedCommandDocument, sharedDocumentFromExport } from './sharedCommandDocument.js'
const registry = new Map(COMMAND_DEFINITIONS.map(command => [command.id, command]))
const options = { commandRegistry: registry, registeredPromptIds: new Set(['navigation.symbol']) }
const empty = () => ({ bindings: [] })
const clone = value => JSON.parse(JSON.stringify(value))
export function projectSharedCommands(raw) {
  const bindings = raw.bindings.map(item => normalizeCommandBindingPreference(item, options)).filter(Boolean)
  return { bindings, macros: [], unavailableCount: raw.bindings.length - bindings.length }
}
function parse(storage, key) { try { return JSON.parse(storage?.getItem(key) || 'null') } catch { return null } }
function validSnapshot(value) {
  return value && Number.isSafeInteger(value.revision) && value.revision >= 0 && Number.isSafeInteger(value.draftRevision) && value.draftRevision >= 0
    && typeof value.initialized === 'boolean' && validSharedCommandDocument(value.document)
    && (value.draft === null || (validSharedCommandDocument(value.draft) && Number.isSafeInteger(value.baseRevision) && value.baseRevision >= 0))
}
export function createSharedCommandSync({ userId, project, environment, serviceUrl, client, storage, onChange, fetcher = fetch }) {
  const key = `erevis-shared-commands-v1:${encodeURIComponent(serviceUrl)}:${encodeURIComponent(project)}:${environment}:${userId}`
  const cached = userId ? parse(storage, key) : null
  let snapshot = validSnapshot(cached?.snapshot) && cached.snapshot.environment === environment ? cached.snapshot : null
  let raw = validSharedCommandDocument(cached?.raw) ? cached.raw : snapshot?.draft || snapshot?.document || empty()
  let dirty = Boolean(snapshot && cached?.dirty), hydrated = false, disposed = false, busy = null, generation = 0, cacheFailed = false, exclusive = false
  let status = userId ? 'loading' : 'signed-out'
  const legacy = sharedDocumentFromExport(parse(storage, 'erevis-command-customization-v1'))
  // Stable bindings keep in-progress keyboard sequences alive across renders.
  let projectedRaw, projected
  function projection() {
    if (projectedRaw !== raw) { projectedRaw = raw; projected = projectSharedCommands(raw) }
    return projected
  }
  const get = () => ({ ...projection(), fullDocument: raw, shared: true, environment, status,
    revision: snapshot?.revision, draftRevision: snapshot?.draftRevision, previousRevision: snapshot?.previousRevision,
    hasDraft: dirty || Boolean(snapshot?.draft), staleDraft: Boolean(snapshot?.draft && snapshot.baseRevision !== snapshot.revision),
    canEdit: Boolean(userId && hydrated && !exclusive && status !== 'conflict' && (snapshot?.initialized || environment === 'production')),
    canPublish: Boolean(environment !== 'production' && hydrated && !dirty && snapshot?.draft && snapshot.baseRevision === snapshot.revision && !['conflict', 'saving', 'offline'].includes(status)),
    invalidCount: 0, legacyAvailable: false, cacheFailed })
  function emit() {
    if (disposed) return
    if (userId) { try { storage?.setItem(key, JSON.stringify({ snapshot, raw, dirty })); cacheFailed = !storage } catch { cacheFailed = true } }
    onChange?.(get())
  }
  function absorb(value, replace = true) {
    if (!validSnapshot(value) || value.environment !== environment) throw Error('Invalid shared profile')
    snapshot = value; hydrated = true
    if (replace) {
      const nextRaw = value.draft || value.document
      if (JSON.stringify(raw) !== JSON.stringify(nextRaw)) raw = clone(nextRaw)
    }
    status = !value.initialized && environment !== 'production' ? 'waiting-source' : value.draft ? 'draft' : 'synced'
    emit()
  }
  async function request(action, document) {
    const endpoint = new URL(serviceUrl)
    if (endpoint.protocol !== 'https:' || endpoint.username || endpoint.password || endpoint.search || endpoint.hash || endpoint.pathname !== '/api/commands/profile') throw Error('Invalid command profile endpoint')
    const { data } = await client.auth.getSession()
    const session = data?.session
    if (!session?.access_token || session.user?.id !== userId) throw Error('Session changed')
    const abort = new AbortController()
    let timer
    try {
      return await Promise.race([(async () => {
        const response = await fetcher(serviceUrl, { method: 'POST', credentials: 'omit', signal: abort.signal,
          headers: { Authorization: `Bearer ${session.access_token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ action, revision: snapshot?.revision ?? 0, draftRevision: snapshot?.draftRevision ?? 0,
            ...(document ? { document } : {}), ...(action === 'restore' ? { restoreRevision: snapshot?.previousRevision } : {}) }),
        })
        const value = await response.json()
        if (!response.ok) {
          const error = Error('Shared profile unavailable')
          if (action === 'read' && response.status === 403 && value?.code === 'PROFILE_NOT_LINKED') error.code = 'PROFILE_NOT_LINKED'
          throw error
        }
        return value
      })(), new Promise((_, reject) => { timer = setTimeout(() => { abort.abort(); reject(Error('Sync timed out')) }, 15000) })])
    } finally { clearTimeout(timer) }
  }
  function serialize(action) {
    if (busy) return busy
    busy = (async () => { try { return await action() } catch (error) { if (!disposed) { status = error.code === 'PROFILE_NOT_LINKED' ? 'unlinked' : 'offline'; emit() }; return false } })().finally(() => { busy = null })
    return busy
  }
  async function flush() {
    while (dirty && !disposed && status !== 'conflict') {
      const sentGeneration = generation
      status = 'saving'; emit()
      const value = await request('save', clone(raw))
      if (disposed) return false
      if (value.conflict) { status = 'conflict'; emit(); return false }
      dirty = generation !== sentGeneration
      absorb(value, !dirty)
    }
    return !dirty
  }
  function refresh() {
    if (!userId || disposed || ['conflict', 'unlinked'].includes(status)) return Promise.resolve(false)
    return serialize(async () => {
      if (dirty) { hydrated = true; return flush() }
      const value = await request('read')
      if (disposed) return false
      if (dirty) return flush() // Preserve the revision against which the edit was made.
      absorb(value)
      // This permission is issued only for an explicitly provisioned source owner.
      // CAS + initialized=false prevents another browser or an empty reset being overwritten.
      if (environment === 'production' && value.bootstrapAllowed && !value.initialized && validSharedCommandDocument(legacy) && legacy.bindings.length) {
        exclusive = true; raw = clone(legacy); status = 'saving'; emit()
        try {
          const seeded = await request('bootstrap', legacy)
          if (disposed) return false
          if (seeded.conflict) { status = 'conflict'; emit(); return false }
          absorb(seeded)
        } finally { exclusive = false; emit() }
      }
      return true
    })
  }
  function edit(update) {
    if (!get().canEdit || disposed) return Promise.resolve(false)
    const visible = projectSharedCommands(raw), next = update({ bindings: visible.bindings, macros: visible.macros })
    const nextRaw = {
      bindings: [...raw.bindings.filter(item => !visible.bindings.some(active => active.id === item.id)), ...next.bindings],
    }
    if (!validSharedCommandDocument(nextRaw)) return Promise.resolve(false)
    raw = clone(nextRaw); dirty = true; generation += 1; emit()
    return serialize(flush)
  }
  function backup() {
    try { storage?.setItem(`${key}:recovery`, JSON.stringify({ snapshot, raw, dirty })); if (!storage) throw Error(); return true }
    catch { cacheFailed = true; emit(); return false }
  }
  function publish() {
    if (!get().canPublish || busy || disposed) return Promise.resolve(false)
    exclusive = true
    return serialize(async () => {
      status = 'saving'; emit()
      const value = await request('publish')
      if (disposed) return false
      if (value.conflict) { status = 'conflict'; emit(); return false }
      absorb(value); return true
    }).finally(() => { exclusive = false; emit() })
  }
  function useAccount() {
    if (!userId || busy || disposed || !backup()) return Promise.resolve(false)
    exclusive = true; status = 'saving'; emit()
    return serialize(async () => {
      const value = await request(environment === 'production' ? 'read' : 'follow')
      if (disposed) return false
      if (value.conflict) { status = 'conflict'; emit(); return false }
      dirty = false; absorb(value); return true
    }).finally(() => { exclusive = false; emit() })
  }
  function restorePrevious() {
    if (environment !== 'production' || !get().canEdit || busy || dirty || !Number.isSafeInteger(snapshot?.previousRevision)) return Promise.resolve(false)
    exclusive = true; status = 'saving'; emit()
    return serialize(async () => {
      const value = await request('restore'); if (disposed) return false
      if (value.conflict) { status = 'conflict'; emit(); return false }
      absorb(value); return true
    }).finally(() => { exclusive = false; emit() })
  }
  function importDocument(value) {
    value = sharedDocumentFromExport(value)
    if (!get().canEdit || busy || !validSharedCommandDocument(value)) return Promise.resolve(false)
    raw = clone(value); dirty = true; generation += 1; emit(); return serialize(flush)
  }
  return { get, refresh, edit, publish, useAccount, restorePrevious, importDocument,
    importLegacy: () => importDocument(legacy), activate: () => { disposed = false }, dispose: () => { disposed = true } }
}
